import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { Clock, Users } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Card } from "~/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { type ViewMode } from "~/hooks/useViewMode";
import { resolveAccentPaletteColor } from "~/lib/accentPalette";
import { stripMarkdownToText } from "~/lib/stripMarkdownToText";
import { cn } from "~/lib/utils";
import { FavoriteButton } from "~/pages/manifold-nextgen/components/FavoriteButton";
import { nextgenProjectPath } from "~/pages/manifold-nextgen/paths";
import { parsePrincipal } from "~/services/principals";
import { ProjectCollaborator, ProjectListItem, projectRouteId } from "~/services/projects";
import { ProjectAccessBadge } from "./ProjectAccessBadge";
import { ProjectIcon } from "./ProjectIcon";

interface ProjectCardProps {
  project: ProjectListItem;
  // Collaborators, loaded in a second phase. `undefined` while in flight.
  collaborators?: ProjectCollaborator[];
  view?: ViewMode;
  favorite?: boolean;
  // When provided, renders a favorite toggle. Omit to hide the affordance entirely.
  onToggleFavorite?: () => void;
}

// A non-favorite toggle stays hidden until the row/card is hovered or the
// button is focused; a favorited one is always visible.
const favoriteVisibility = (favorite: boolean) =>
  !favorite && "opacity-0 group-hover:opacity-100 focus-visible:opacity-100";

const MAX_PEOPLE_AVATARS = 2;

const collaboratorName = (collaborator: ProjectCollaborator): string => {
  const { isGroup, id } = parsePrincipal(collaborator.principal);
  if (isGroup) {
    return collaborator.display_name || id;
  }
  return [collaborator.given_name, collaborator.family_name].filter(Boolean).join(" ") || id;
};

const PeopleAvatars = ({
  collaborators,
  ringClass,
}: {
  collaborators: ProjectCollaborator[] | undefined;
  ringClass: string;
}) => {
  if (collaborators === undefined) {
    // Collaborators resolve after the card; hold the slot with a skeleton avatar.
    return (
      <span
        aria-hidden="true"
        className={cn("inline-flex size-6 animate-pulse rounded-full bg-muted ring-2", ringClass)}
      />
    );
  }
  if (collaborators.length === 0) return null;
  const overflow = collaborators.length - MAX_PEOPLE_AVATARS;
  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center">
        {collaborators.slice(0, MAX_PEOPLE_AVATARS).map((collaborator, i) => {
          const { isGroup } = parsePrincipal(collaborator.principal);
          return (
            <Tooltip key={collaborator.principal}>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    `relative inline-flex ring-2 ${ringClass} rounded-full transition-transform duration-75 hover:z-10 hover:scale-110`,
                    i > 0 && "-ml-1"
                  )}
                >
                  {isGroup ? (
                    <span className="flex size-6 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Users className="size-3.5" aria-hidden="true" />
                    </span>
                  ) : (
                    <Avatar className="size-6">
                      <AvatarImage src={collaborator.image_url ?? undefined} />
                      <AvatarFallback>
                        {collaboratorName(collaborator).charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  )}
                </span>
              </TooltipTrigger>
              <TooltipContent>{collaboratorName(collaborator)}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
      {overflow > 0 && <span className="text-xs text-muted-foreground">+{overflow}</span>}
    </div>
  );
};

export const ProjectCard = ({
  project,
  collaborators,
  view = "card",
  favorite = false,
  onToggleFavorite,
}: ProjectCardProps) => {
  const navigate = useNavigate();

  const lastActivity = formatDistanceToNowStrict(new Date(project.last_activity_at), {
    addSuffix: true,
  });
  const description = useMemo(
    () => (project.description ? stripMarkdownToText(project.description) : ""),
    [project.description]
  );

  if (view === "list") {
    return (
      <div className="group relative">
        <div
          className={cn(
            "flex items-center gap-4 rounded-lg bg-card px-4 py-5",
            "cursor-pointer transition-colors duration-150 group-hover:bg-muted"
          )}
          onClick={() => navigate(nextgenProjectPath(projectRouteId(project)))}
        >
          <ProjectIcon project={project} size={20} />
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold text-card-foreground">{project.name}</h3>
            <p
              className={cn(
                "truncate text-xs text-muted-foreground",
                !description && "italic text-muted-foreground/60"
              )}
            >
              {description || "No description"}
            </p>
          </div>
          <div className="w-20 shrink-0">
            <ProjectAccessBadge accessLevel={project.access_level} hideDefault />
          </div>
          <div className="flex w-36 shrink-0 items-center gap-2">
            <PeopleAvatars collaborators={collaborators} ringClass="ring-card" />
          </div>
          <p className="flex w-36 shrink-0 items-center justify-end gap-1 text-xs text-muted-foreground">
            <Clock className="size-3.5" aria-hidden="true" />
            {lastActivity}
          </p>
          {onToggleFavorite && (
            <FavoriteButton
              favorite={favorite}
              onToggle={onToggleFavorite}
              className={cn("shrink-0", favoriteVisibility(favorite))}
            />
          )}
        </div>
      </div>
    );
  }

  const accentColor = resolveAccentPaletteColor(project.accent_color ?? undefined);

  return (
    <div className="group relative h-full transition-transform duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] hover:scale-[1.02]">
      <Card
        className={cn(
          "relative flex h-full min-h-80 gap-0 overflow-hidden rounded-l-lg p-0",
          "cursor-pointer shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
        )}
        onClick={() => navigate(nextgenProjectPath(projectRouteId(project)))}
      >
        <div
          className="absolute inset-y-0 left-0 w-10 overflow-hidden rounded-l-lg"
          style={{ backgroundColor: accentColor.bg }}
          aria-hidden="true"
        />
        {onToggleFavorite && (
          <FavoriteButton
            favorite={favorite}
            onToggle={onToggleFavorite}
            className={cn("absolute right-2.5 top-2.5 z-20", favoriteVisibility(favorite))}
          />
        )}
        <div className="ml-10 flex flex-1 flex-col justify-between gap-3">
          <div className="flex flex-col gap-3 px-5 pt-5 pb-4">
            <h3 className="truncate pr-7 text-base font-semibold leading-tight text-card-foreground group-hover:text-foreground">
              {project.name}
            </h3>

            <p
              className={cn(
                "line-clamp-2 min-h-[2.5rem] text-sm text-muted-foreground",
                !description && "italic text-muted-foreground/60"
              )}
            >
              {description || "No description"}
            </p>
          </div>

          <div className="flex flex-col gap-2 px-5 py-2.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Shared with</span>
              <PeopleAvatars collaborators={collaborators} ringClass="ring-card" />
            </div>
            <div className="flex items-center gap-3">
              <ProjectAccessBadge accessLevel={project.access_level} hideDefault />
              <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="size-3.5" aria-hidden="true" />
                {lastActivity}
              </span>
            </div>
          </div>
        </div>
      </Card>

      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 rounded-xl",
          "opacity-0 shadow-[0_4px_10px_rgba(15,23,42,0.06)]",
          "transition-opacity duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] group-hover:opacity-100"
        )}
      />
    </div>
  );
};
